import { type FC } from "react";
import { Accordion, AccordionHeader, AccordionItem, AccordionBody } from "react-bootstrap";

import { useInquiryConstants, type InquiryAppointment } from "../../../../hooks/use-inquiries";
import { SortableTable, SortableTableHeader } from "../../../table";
import { AppointmentDateTime } from "./appointment-date-time";
import { useAccessControl } from "../../../../app/use-access-control";
import { useCenterConstants } from "../../../../hooks/use-center";

interface AppointmentHistoryAccordionProps {
  appointmentHistory?: InquiryAppointment[];
  className?: string;
  defaultActiveKey?: string;
}

export const AppointmentHistoryAccordion: FC<AppointmentHistoryAccordionProps> = ({
  appointmentHistory = [],
  className,
  defaultActiveKey
}) => {
  const MAX_OTHER_REASON_TEXT_LENGTH = 250;
  const { activeCenterId } = useAccessControl();
  const inquiryConstantsQuery = useInquiryConstants();
  const centerConstantsQuery = useCenterConstants(activeCenterId);

  const getCancellationReason = (appointment: InquiryAppointment) => {
    const otherReasonText = appointment.cancellationReasonOther?.trim();
    const reasonName = inquiryConstantsQuery.data?.appointmentCancelChoices.find(
      reason => reason.value === appointment.reasonToCancelId
    )?.name;

    if (!reasonName) {
      if (otherReasonText) {
        return `Other - ${otherReasonText.slice(0, MAX_OTHER_REASON_TEXT_LENGTH)}`;
      }
      return undefined;
    }

    if (reasonName.toLowerCase() !== "other") {
      return reasonName;
    }

    if (!otherReasonText) {
      return "Other";
    }

    return `Other - ${otherReasonText.slice(0, MAX_OTHER_REASON_TEXT_LENGTH)}`;
  };

  const headers: SortableTableHeader<InquiryAppointment>[] = [
    {
      renderKey: "appointmentType",
      name: "Appointment Type",
      render: item => centerConstantsQuery.activeConstants.appointmentTypeLookup[item.typeId],
      tableDataCellProps: {
        isPrimary: true
      }
    },
    {
      renderKey: "scheduledTime",
      name: "Scheduled Time",
      render: item => <AppointmentDateTime appointment={item} />
    },
    {
      renderKey: "status",
      name: "Status",
      render: item => {
        const status = inquiryConstantsQuery.data?.appointmentStatusesLookup[item.statusId];
        const isCancelled =
          item.statusId === inquiryConstantsQuery.data?.APPOINTMENT_STATUS_CANCELLED_ID || status === "Cancelled";

        if (!isCancelled) {
          return status;
        }

        const cancellationReason = getCancellationReason(item);
        if (!cancellationReason) {
          return status;
        }

        return (
          <>
            <div>{status}</div>
            <div>({cancellationReason})</div>
          </>
        );
      }
    }
  ];

  return (
    <Accordion defaultActiveKey={defaultActiveKey} className={className}>
      <AccordionItem eventKey="0">
        <AccordionHeader as="h3">Appointment History</AccordionHeader>
        <AccordionBody>
          <SortableTable tableCollapseScreenSize="sm" headers={headers} data={appointmentHistory} uniqueIdKey="id" />
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
};
